import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, Trash2, Gift, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';

const formatPrice = (price) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0
  }).format(price);
};

// Packaging selector component
const PackagingSelector = ({ packagingConfig, packagingType, setPackagingType, canSelectSmallBox, packagingInfo }) => {
  const [showImages, setShowImages] = useState(false);
  
  if (!packagingConfig) return null;
  
  const images = packagingConfig.images || [];
  
  return (
    <div className="bg-gradient-to-r from-[#1E1E1E] to-[#2a2a2a] rounded-xl p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Gift size={18} className="text-yellow-400" />
          <span className="font-heading text-sm">PRESENTACIÓN PREMIUM</span>
        </div>
        <button 
          onClick={() => setShowImages(!showImages)}
          className="text-yellow-400 text-xs flex items-center gap-1 hover:underline"
        >
          Ver caja {showImages ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>
      
      {/* Images preview */}
      {showImages && images.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {images.map((img, i) => (
            <img 
              key={i} 
              src={img} 
              alt={`Caja premium ${i + 1}`}
              className="w-full h-20 object-cover rounded-lg border border-yellow-400/30"
            />
          ))}
        </div>
      )}
      
      <p className="text-xs text-gray-300 mb-3">
        {packagingConfig.description || 'Caja negra con virutas de madera, envuelta en papel/tela negro con cintas doradas.'}
      </p>
      
      {/* Options */}
      <div className="space-y-2">
        <label className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${packagingType === 'none' ? 'bg-white/10' : 'hover:bg-white/5'}`}>
          <input 
            type="radio" 
            name="packaging" 
            value="none" 
            checked={packagingType === 'none'}
            onChange={() => setPackagingType('none')}
            className="accent-yellow-400"
          />
          <Package size={16} className="text-gray-400" />
          <div className="flex-1">
            <span className="text-sm">Sin caja premium</span>
            <p className="text-xs text-gray-400">Envasado al vacío con etiqueta</p>
          </div>
          <span className="text-sm text-gray-400">Incluido</span>
        </label>
        
        <label className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${packagingType === 'normal' ? 'bg-yellow-400/20 border border-yellow-400/50' : 'hover:bg-white/5'}`}>
          <input 
            type="radio" 
            name="packaging" 
            value="normal" 
            checked={packagingType === 'normal'}
            onChange={() => setPackagingType('normal')}
            className="accent-yellow-400"
          />
          <Gift size={16} className="text-yellow-400" />
          <div className="flex-1">
            <span className="text-sm">Caja Premium</span>
            <p className="text-xs text-gray-400">1 caja cada 2 productos</p>
          </div>
          <span className="text-sm text-yellow-400">{formatPrice(packagingConfig.normal_box_price || 1500)}/caja</span>
        </label>
        
        {canSelectSmallBox && (
          <label className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${packagingType === 'small' ? 'bg-yellow-400/20 border border-yellow-400/50' : 'hover:bg-white/5'}`}>
            <input 
              type="radio" 
              name="packaging" 
              value="small" 
              checked={packagingType === 'small'}
              onChange={() => setPackagingType('small')}
              className="accent-yellow-400"
            />
            <Gift size={16} className="text-yellow-400" />
            <div className="flex-1">
              <span className="text-sm">Caja Cuadrada (Quesos)</span>
              <p className="text-xs text-gray-400">1 caja por queso</p>
            </div>
            <span className="text-sm text-yellow-400">{formatPrice(packagingConfig.small_box_price || 2000)}/caja</span>
          </label>
        )}
      </div>
      
      {/* Selected packaging summary */}
      {packagingType !== 'none' && packagingInfo.cost > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10 flex justify-between items-center">
          <span className="text-xs text-gray-300">{packagingInfo.description}</span>
          <span className="text-sm text-yellow-400 font-medium">+{formatPrice(packagingInfo.cost)}</span>
        </div>
      )}
    </div>
  );
};

export const CartDrawer = () => {
  const { 
    items, isOpen, closeCart, updateQuantity, removeItem, getTotal,
    packagingType, setPackagingType, packagingConfig, getPackagingInfo, canSelectPackaging, canSelectSmallBox
  } = useCart();

  const packagingInfo = getPackagingInfo();
  const subtotal = getTotal();
  const totalWithPackaging = subtotal + packagingInfo.cost;

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={closeCart}
        data-testid="cart-overlay"
      />

      {/* Drawer */}
      <div
        className="fixed right-0 top-0 h-full w-full max-w-md bg-[#EFE1C4] shadow-lg z-50 flex flex-col"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[hsl(var(--border))]">
          <h2 className="font-heading text-lg text-[#1E1E1E]">TU CARRITO</h2>
          <button
            onClick={closeCart}
            className="p-2 text-[#1E1E1E] hover:text-[#7A5A2E]"
            aria-label="Cerrar carrito"
          >
            <X size={24} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-body text-[#4E3A21] opacity-80 mb-4">
                Tu carrito está vacío
              </p>
              <Link to="/productos" onClick={closeCart}>
                <Button className="btn-outline-warm rounded-[10px]">
                  Ver productos
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.slug}
                  className="flex gap-4 bg-[#F5EBD4] rounded-lg p-3"
                  data-testid={`cart-item-${item.slug}`}
                >
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded"
                    width="80"
                    height="80"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="flex-1">
                    <h3 className="font-body text-sm font-medium text-[#1E1E1E]">
                      {item.name}
                    </h3>
                    <p className="font-body text-sm text-[#7A5A2E] mb-2">
                      {formatPrice(item.price)}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.slug, item.quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center border border-[#8C6A3F] rounded text-[#4E3A21] hover:bg-[#EFE1C4]"
                        aria-label="Reducir cantidad"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="font-body text-sm text-[#1E1E1E] w-8 text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.slug, item.quantity + 1)}
                        className="w-7 h-7 flex items-center justify-center border border-[#8C6A3F] rounded text-[#4E3A21] hover:bg-[#EFE1C4]"
                        aria-label="Aumentar cantidad"
                      >
                        <Plus size={14} />
                      </button>
                      <button
                        onClick={() => removeItem(item.slug)}
                        className="ml-auto p-1 text-[#4E3A21] hover:text-red-600"
                        aria-label="Eliminar producto"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[hsl(var(--border))] p-4 space-y-4">
            {/* Packaging Selector */}
            {canSelectPackaging() && (
              <PackagingSelector 
                packagingConfig={packagingConfig}
                packagingType={packagingType}
                setPackagingType={setPackagingType}
                canSelectSmallBox={canSelectSmallBox()}
                packagingInfo={packagingInfo}
              />
            )}
            
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="font-body text-[#4E3A21]">Subtotal productos:</span>
                <span className="font-body text-[#1E1E1E]">{formatPrice(subtotal)}</span>
              </div>
              {packagingInfo.cost > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="font-body text-[#4E3A21]">Packaging premium:</span>
                  <span className="font-body text-[#1E1E1E]">{formatPrice(packagingInfo.cost)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-[hsl(var(--border))]">
                <span className="font-body text-[#1E1E1E]">Total:</span>
                <span className="font-heading text-lg text-[#1E1E1E]">
                  {formatPrice(totalWithPackaging)}
                </span>
              </div>
            </div>
            <p className="font-body text-xs text-[#4E3A21] opacity-70">
              Envío calculado en el checkout
            </p>
            <Link to="/checkout" onClick={closeCart}>
              <Button className="w-full btn-primary-warm rounded-[10px] h-11" data-testid="checkout-button">
                Finalizar compra
              </Button>
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
